import { useMemo, useState } from "react";
import { Plus, Sparkles } from "lucide-react";

import Avatar from "../components/Avatar";
import BottomNav from "../components/BottomNav";
import EntryCard from "../components/EntryCard";
import EntryDetailSheet from "../components/EntryDetailSheet";
import EntryFormSheet from "../components/EntryFormSheet";
import { TYPE_ORDER, TYPE_STYLES } from "../utils/constants";
import { summarize } from "../utils/helpers";
import { SHADOW, TOKENS as T } from "../styles/styles";

export default function TrackerPage({
  activeGroup,
  entries,
  allEntries = [],
  profile,
  filter,
  sortBy,
  showForm,
  draft,
  currentUser,
  onFilterChange,
  onSortChange,
  onToggleForm,
  onDraftChange,
  onSaveEntry,
  onCancelEntry,
  onEditEntry,
  onRemoveEntry,
  onNavigate,
}) {
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Members and summary figures are always derived from the group's FULL entry
  // list, never the filtered view — otherwise "Total" would drop whenever a
  // type filter is active.
  const members = useMemo(() => {
    const unique = new Map();

    if (currentUser?.uid) {
      unique.set(currentUser.uid, {
        uid: currentUser.uid,
        label: profile?.displayName || currentUser.email || "You",
        avatarId: profile?.avatarId,
      });
    }

    allEntries.forEach((entry) => {
      const member = entry.addedBy;
      if (!member?.uid || unique.has(member.uid)) return;

      unique.set(member.uid, {
        uid: member.uid,
        label: member.displayName || member.email || "Member",
        avatarId: null,
      });
    });

    return Array.from(unique.values());
  }, [allEntries, currentUser, profile]);

  const summary = useMemo(() => summarize(allEntries), [allEntries]);

  const stats = [
    { label: "Total", value: summary.total, color: T.primary },
    { label: "Active", value: summary.inProgress, color: "#3B82F6" },
    { label: "Done", value: summary.completed, color: "#10B981" },
    { label: "Planned", value: summary.planned, color: "#F59E0B" },
  ];

  const filterTabs = [
    { key: "all", label: "All" },
    ...TYPE_ORDER.map((type) => ({
      key: type,
      label: TYPE_STYLES[type].label,
    })),
  ];

  return (
    <div style={pageStyle}>
      <div style={headerShellStyle}>
        <div style={headerRowStyle}>
          <div>
            <p style={groupLabelStyle}>{activeGroup?.name || "Personal"}</p>
            <h1 style={screenTitleStyle}>Tracker</h1>
          </div>

          {/* The Figma header carries a bell, but notifications are out of scope
              and it previously triggered a silent logout — a misleading control.
              Logout now lives on the Profile tab, where users expect it. */}
          <div style={headerActionsStyle}>
            <div style={avatarStackStyle}>
              {members.slice(0, 4).map((member) => (
                <div key={member.uid} style={stackedAvatarStyle}>
                  <Avatar
                    avatarId={member.avatarId}
                    fallbackKey={member.uid}
                    initial={member.label[0]}
                    size={30}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={statsGridStyle}>
          {stats.map((stat) => (
            <div key={stat.label} style={statCardStyle}>
              <p style={{ ...statValueStyle, color: stat.color }}>
                {stat.value}
              </p>
              <p style={statLabelStyle}>{stat.label}</p>
            </div>
          ))}
        </div>

        <div style={filterRowStyle} className="scrollbar-hide">
          {filterTabs.map((tab) => {
            const isActive = filter === tab.key;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => onFilterChange(tab.key)}
                style={{
                  ...filterChipStyle,
                  background: isActive ? T.primary : T.card,
                  color: isActive ? T.primaryForeground : T.mutedForeground,
                  boxShadow: isActive ? "none" : SHADOW.xs,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Sorting has no counterpart in the Figma Tracker, but the feature must
            keep working — rendered in the design's chip idiom. */}
        <div style={sortRowStyle}>
          <select
            value={sortBy}
            onChange={(event) => onSortChange(event.target.value)}
            style={sortSelectStyle}
          >
            <option value="newest">Newest first</option>
            <option value="rating">Highest rating</option>
            <option value="status">Status</option>
            <option value="user">Added by</option>
          </select>
        </div>
      </div>

      <div style={listShellStyle} className="scrollbar-hide">
        {entries.length === 0 ? (
          <div style={emptyStateStyle}>
            <div style={emptyIconBoxStyle}>
              <Sparkles size={28} color={T.mutedForeground} />
            </div>
            <p style={emptyTitleStyle}>Nothing tracked yet</p>
            <p style={emptySubtitleStyle}>
              Tap + to add the first thing you're watching, reading or listening
              to.
            </p>
          </div>
        ) : (
          entries.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              currentUser={currentUser}
              onTap={setSelectedEntry}
            />
          ))
        )}
      </div>

      <button type="button" onClick={onToggleForm} style={fabStyle}>
        <Plus size={24} color="#fff" />
      </button>

      <BottomNav active="tracker" onNavigate={onNavigate} />

      <EntryDetailSheet
        entry={selectedEntry}
        currentUser={currentUser}
        onClose={() => setSelectedEntry(null)}
        onEditEntry={onEditEntry}
        onRemoveEntry={onRemoveEntry}
      />

      {showForm && (
        <EntryFormSheet
          draft={draft}
          onDraftChange={onDraftChange}
          onSaveEntry={onSaveEntry}
          onCancel={onCancelEntry}
        />
      )}
    </div>
  );
}

const pageStyle = {
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  background: T.background,
  color: T.foreground,
};

const headerShellStyle = {
  padding: "8px 20px 12px",
  flexShrink: 0,
};

const headerRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 16,
};

const groupLabelStyle = {
  margin: "0 0 2px",
  fontSize: 11,
  fontWeight: 600,
  color: T.mutedForeground,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
};

const screenTitleStyle = {
  margin: 0,
  fontSize: 26,
  fontWeight: 800,
  lineHeight: 1.15,
  color: T.foreground,
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const avatarStackStyle = {
  display: "flex",
  alignItems: "center",
};

const stackedAvatarStyle = {
  marginLeft: -8,
  borderRadius: 12,
  boxShadow: `0 0 0 2px ${T.card}`,
  lineHeight: 0,
};

const statsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 8,
  marginBottom: 16,
};

const statCardStyle = {
  background: T.card,
  borderRadius: 16,
  padding: "10px 8px",
  textAlign: "center",
  boxShadow: SHADOW.sm,
};

const statValueStyle = {
  margin: 0,
  fontSize: 18,
  fontWeight: 800,
  lineHeight: 1,
};

const statLabelStyle = {
  margin: "2px 0 0",
  fontSize: 10,
  fontWeight: 500,
  color: T.mutedForeground,
};

const filterRowStyle = {
  display: "flex",
  gap: 8,
  overflowX: "auto",
  paddingBottom: 2,
};

const filterChipStyle = {
  flexShrink: 0,
  padding: "8px 16px",
  borderRadius: 999,
  border: "none",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s ease",
  fontFamily: "inherit",
};

const sortRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 10,
};

const sortSelectStyle = {
  padding: "7px 12px",
  borderRadius: 999,
  border: "none",
  background: T.card,
  boxShadow: SHADOW.xs,
  color: T.mutedForeground,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};

const listShellStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "0 20px 150px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const emptyStateStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: 208,
  textAlign: "center",
};

const emptyIconBoxStyle = {
  width: 64,
  height: 64,
  borderRadius: 24,
  background: T.muted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 12,
};

const emptyTitleStyle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: T.foreground,
};

const emptySubtitleStyle = {
  margin: "4px 0 0",
  fontSize: 13,
  color: T.mutedForeground,
};

const fabStyle = {
  position: "fixed",
  right: 20,
  bottom: 128,
  width: 56,
  height: 56,
  borderRadius: 24,
  border: "none",
  background: T.primary,
  boxShadow: SHADOW.fab,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 20,
};
